import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ChevronDown, Clock, Quote, ShoppingBag } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { api } from "../lib/api";
import Icon from "../components/Icon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import { TID } from "../constants/testIds";

export default function GuideDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/guides/${slug}`)
      .then(({ data }) => setGuide(data))
      .catch(() => setGuide(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-32 text-center text-white/40 font-mono-label">
        {t.common.loading}
      </div>
    );
  }
  if (!guide) {
    return (
      <div className="container mx-auto px-6 py-32 text-center">
        <p className="font-display text-3xl mb-6">404</p>
        <Link to={lp("/guides")} className="btn-ghost inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> {t.common.back}
        </Link>
      </div>
    );
  }
  const tr = guide.translations[lang];

  const onPurchase = async () => {
    try {
      // Country detection: use browser locale heuristic
      const localeCountry = (navigator.language || "").split("-")[1] || "";
      const { data } = await api.post("/checkout/session", {
        slug: guide.slug,
        origin: window.location.origin,
        language: lang,
        country: localeCountry,
      });
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(lang === "en" ? "Could not start checkout." : "Nu am putut iniția plata.");
      }
    } catch (e) {
      toast.error(
        lang === "en"
          ? "Checkout error. Please try again or contact hello@thelifeplot.com."
          : "Eroare la plată. Încearcă din nou sau scrie la hello@thelifeplot.com."
      );
    }
  };

  return (
    <div className="container mx-auto px-6 py-12 md:py-16">
      <Link to={lp("/guides")} className="font-mono-label text-white/40 hover:text-white inline-flex items-center gap-2 mb-12">
        <ArrowLeft className="h-3.5 w-3.5" /> {lang === "en" ? "All guides" : "Toate ghidurile"}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* LEFT: content */}
        <article className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
              <Icon name={guide.icon} className="h-5 w-5 text-gold" />
            </div>
            <span className="font-mono-label text-white/50">
              {t.categoryLabel} · {guide.category}
            </span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]"
          >
            {tr.title}
          </motion.h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl">{tr.subtitle}</p>

          <div className="mt-8 flex items-center gap-6 text-xs font-mono-label text-white/40">
            <span className="inline-flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" /> {guide.reading_time_min} {t.guide.minutes}
            </span>
          </div>

          <div className="divider-fade my-12" />

          <Section label={t.guide.whatsInside}>
            <ul className="space-y-3">
              {tr.whats_inside.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80">
                  <Check className="h-4 w-4 text-gold mt-1 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section label={t.guide.preview}>
            <blockquote className="ed-italic text-2xl md:text-3xl leading-snug text-white/85 border-l-2 border-gold/40 pl-6">
              "{tr.preview}"
            </blockquote>
          </Section>

          <Section label={t.guide.actionPlan}>
            <ol className="space-y-3">
              {tr.action_plan.map((step, i) => (
                <li
                  key={i}
                  className="glass rounded-xl px-5 py-4 flex items-center gap-4"
                >
                  <span className="font-mono-label text-gold w-6">0{i + 1}</span>
                  <span className="text-white/85">{step}</span>
                </li>
              ))}
            </ol>
          </Section>

          <Section label={t.guide.exercises}>
            <ul className="space-y-3">
              {tr.exercises.map((ex, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="font-mono-label text-gold mt-1">{i + 1}.</span>
                  <span className="text-white/80">{ex}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section label={t.guide.checklist}>
            <ul className="space-y-2">
              {tr.checklist.map((c, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="h-5 w-5 rounded-md border border-white/15 inline-block" />
                  <span className="text-white/75">{c}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section label={t.guide.faq}>
            <Accordion type="single" collapsible className="w-full">
              {tr.faq.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left text-base md:text-lg font-display">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/65">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Section>

          <Section label={t.guide.testimonials}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tr.testimonials.map((r, i) => (
                <div key={i} className="glass rounded-2xl p-6">
                  <Quote className="h-4 w-4 text-gold mb-4" />
                  <p className="text-white/85">{r.quote}</p>
                  <div className="mt-5">
                    <div className="font-display text-sm">{r.name}</div>
                    <div className="font-mono-label text-white/40">{r.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </article>

        {/* RIGHT: sticky purchase card */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <div className="glass rounded-3xl p-8">
              <div
                className="aspect-[4/3] rounded-2xl mb-6 relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, rgba(212,175,55,0.18), rgba(9,9,11,0.6)), url(${guide.cover_url})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="absolute inset-0 flex items-end p-5">
                  <div>
                    <p className="font-mono-label text-gold/90">The Life Plot</p>
                    <p className="font-display text-xl text-white drop-shadow-lg">{tr.title}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-4xl">€{guide.price.toFixed(0)}</span>
                <span className="font-mono-label text-white/40">{t.guide.currency}</span>
              </div>
              <p className="text-xs text-white/40 mb-6">
                {lang === "en" ? "One-time. Lifetime access." : "O singură plată. Acces pe viață."}
              </p>

              <button
                data-testid={TID.guidePurchaseBtn}
                onClick={onPurchase}
                className="btn-gold w-full inline-flex items-center justify-center gap-2"
              >
                <ShoppingBag className="h-4 w-4" />
                {t.guide.purchase}
              </button>

              <ul className="mt-8 space-y-3 text-sm text-white/65">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {lang === "en" ? "Beautiful PDF, EN + RO" : "PDF frumos, EN + RO"}</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {lang === "en" ? "Apple/Google Pay, cards" : "Apple/Google Pay, carduri"}</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {lang === "en" ? "Instant email delivery" : "Livrare instant pe email"}</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <section className="mb-16">
      <h2 className="font-mono-label text-gold/80 mb-6">{label}</h2>
      {children}
    </section>
  );
}
